import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { cx } from "../cx";
import { Icon } from "../icons/Icon";

/** `sub`: la sublínea de la opción (`sk-option-sub`) — «2 empresas», «vacía:
    sin movimientos». Solo en la lista: el botón enseña la etiqueta sola. */
export interface SelectOption<V extends string> { value: V; label: ReactNode; sub?: ReactNode | undefined }
export interface SelectProps<V extends string> {
  options: ReadonlyArray<SelectOption<V>>;
  /** "" = sin elegir: se pinta el placeholder (alta del Despacho). */
  value: V | "";
  onChange: (v: V) => void;
  ariaLabel?: string | undefined;
  /** Etiqueta externa (`sk-label` de un campo); manda sobre `ariaLabel`. */
  labelledBy?: string | undefined;
  describedBy?: string | undefined;
  placeholder?: ReactNode | undefined;
  required?: boolean | undefined;
  invalid?: boolean | undefined;
  /** Apagado CON PORQUÉ (§5): sigue a la vista y alcanzable a hover, el
      tooltip y la glosa dicen el motivo, y no abre. Fuera del teclado. */
  stale?: boolean | undefined;
  tip?: string | undefined;
  tipPos?: "bottom" | "right" | "left" | undefined;
  id?: string | undefined;
  style?: CSSProperties | undefined;
}

/**
 * Select del sistema (listbox propio — el nativo está PROHIBIDO). Controlado.
 * Teclado completo (flechas, Inicio/Fin, Enter, Escape) y vuelco hacia arriba
 * (`sk-select--up`) cuando abajo no cabe, como la canónica del Despacho.
 */
export function Select<V extends string>({ options, value, onChange, ariaLabel, labelledBy, describedBy, placeholder, required, invalid, stale, tip, tipPos, id, style }: SelectProps<V>) {
  const [open, setOpen] = useState(false);
  const [up, setUp] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const box = useRef<HTMLDivElement>(null);

  const close = useCallback((focus?: boolean) => { setOpen(false); setUp(false); if (focus === true) btn.current?.focus(); }, []);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) close(); };
    // el Escape del listbox es SUYO; el diálogo que lo contiene lo respeta
    // mirando si hay un `.sk-select.sk-open` antes de cerrarse
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") { e.preventDefault(); close(true); } };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", away); document.removeEventListener("keydown", esc); };
  }, [open, close]);

  const opts = () => Array.from(box.current?.querySelectorAll<HTMLButtonElement>(".sk-option") ?? []);
  /** Al abrir, el foco cae en la opción elegida (o la primera) — en un efecto,
      no en un rAF suelto: así React lo cancela al desmontar y ningún foco
      tardío aterriza en un nodo ya fuera del documento. */
  useEffect(() => {
    if (!open) return;
    const list = opts();
    (list.find((o) => o.getAttribute("aria-selected") === "true") ?? list[0])?.focus();
  }, [open]);
  const show = () => {
    const b = btn.current?.getBoundingClientRect();
    const h = box.current?.offsetHeight ?? 0;
    setUp(b !== undefined && b.bottom + h + 16 > window.innerHeight && b.top - h - 16 > 0);
    setOpen(true);
  };
  const move = (from: number, step: number) => { const list = opts(); list[(from + step + list.length) % list.length]?.focus(); };

  const current = options.find((o) => o.value === value);
  return (
    <div className={cx("sk-select", open && "sk-open", up && "sk-select--up")} ref={ref} style={style}>
      <button
        type="button" id={id} ref={btn} className="sk-select-btn"
        aria-haspopup="listbox" aria-expanded={open}
        aria-label={labelledBy === undefined ? ariaLabel : undefined}
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
        aria-required={required === true ? true : undefined}
        aria-invalid={invalid === true ? true : undefined}
        data-sk-tip={tip} data-sk-tip-pos={tip === undefined ? undefined : tipPos}
        {...(stale === true && { "aria-disabled": true as const, tabIndex: -1 })}
        onClick={() => { if (stale !== true) (open ? close() : show()); }}
        onKeyDown={(e) => { if (stale !== true && (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ")) { e.preventDefault(); show(); } }}
      >
        <span className={cx(current === undefined && "sk-placeholder")}>{current?.label ?? placeholder}</span>
        <Icon name="chevron-d" className="sk-chevron" />
      </button>
      <div className="sk-listbox" role="listbox" ref={box} aria-label={ariaLabel ?? undefined}>
        {options.map((o, i) => (
          <button
            type="button" key={o.value} className="sk-option" role="option" aria-selected={o.value === value}
            onClick={() => { onChange(o.value); close(true); }}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") { e.preventDefault(); move(i, 1); }
              else if (e.key === "ArrowUp") { e.preventDefault(); move(i, -1); }
              else if (e.key === "Home") { e.preventDefault(); move(0, 0); }
              else if (e.key === "End") { e.preventDefault(); move(options.length - 1, 0); }
              else if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onChange(o.value); close(true); }
            }}
          >
            {o.label}
            {o.sub !== undefined && <span className="sk-option-sub">{o.sub}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}
